import { useState } from 'react';
import { ClickAwayListener } from '@mui/material';
import { noop } from 'lodash';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { clsx } from 'clsx';
import {
  dropDownWrapper,
  dropDownList,
  visible,
  componentWrapper,
  dropDownControl,
  rowLayout,
  statusSlot,
} from './dropDownMultiselect.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SearchInput } from '@design-system/components/searchinput';
import { Checkbox } from '@design-system/components/checkbox/checkbox';
import { Typography } from '@design-system/components/typography/typography';
import { Tooltip } from '@design-system/components/tooltip/tooltip';
import { sprinkles } from '@design-system/sprinkles.css';

import { StatusTag } from '@client/new/pages/programs/view/components/StatusTag';
import { ProgramReadinessPopover } from '@client/new/pages/programs/view/components/ProgramReadinessPopover';
import {
  ProgramReadiness,
  ProgramStateKind,
} from '@client/new/pages/programs/model/programs.types';

export type DropDownStatus = {
  state: ProgramStateKind;
  readiness: ProgramReadiness | null;
};

type Option<T> = {
  name: string;
  id: T;
  disabled?: boolean;
  /**
   * Optional tooltip shown when the option is disabled. Used by U9 to
   * explain which readiness criteria a program is missing.
   */
  disabledTooltip?: string;
  /**
   * Optional status indicator (e.g. for program rows). Renders the shared
   * `StatusTag` pill on the right side of the option. Omit on non-program
   * dropdowns (features, content) where status has no meaning.
   */
  status?: DropDownStatus;
};

type DropDownMultiselectProps<T> = {
  items: Array<Option<T>>;
  menuClassName?: string;
  disabled?: boolean;
  value: T[];
  onChange: (value: T[]) => void;
  toFormOnChange: (value: T, selectedValues: T[]) => T[];
};

export const DropDownMultiselect = <T extends string | number>(
  p: DropDownMultiselectProps<T>,
) => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverReadiness, setPopoverReadiness] =
    useState<ProgramReadiness | null>(null);
  const popoverOpen = popoverAnchor !== null && popoverReadiness !== null;

  const openPopover = (anchor: HTMLElement, readiness: ProgramReadiness) => {
    setPopoverAnchor(anchor);
    setPopoverReadiness(readiness);
  };
  const closePopover = () => setPopoverAnchor(null);

  // While the readiness popover is open, defer close decisions to it — the
  // popover renders in a portal outside this wrapper, so a click on its
  // paper would normally fire ClickAwayListener and collapse the dropdown.
  // Keeping the dropdown open behind the popover is the expected UX.
  const handleClickAway = () => {
    if (popoverOpen) return;
    setOpen(false);
  };

  const options = p.items.filter((o) =>
    o.name
      .toString()
      .toLocaleLowerCase()
      .includes(searchText.toLocaleLowerCase()),
  );

  return (
    <>
      <ClickAwayListener onClickAway={handleClickAway}>
        <div
          className={clsx(
            componentWrapper,
            p.disabled && sprinkles({ color: 'N-60' }),
          )}
        >
          <div
            className={dropDownControl}
            onClick={() => !p.disabled && setOpen((prev) => !prev)}
          >
            <Typography variant="body" size="large">
              Select
            </Typography>
            <FontAwesomeIcon icon={faCaretDown} />
          </div>
          <div
            className={clsx(open && visible, dropDownWrapper, p?.menuClassName)}
          >
            <SearchInput
              placeholder="Search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onClear={() => setSearchText('')}
              disabled={!!p.disabled}
            />
            <div className={dropDownList}>
              {options.map(
                ({ name, id, disabled, disabledTooltip, status }) => {
                  const row = (
                    <div
                      key={id}
                      className={clsx(
                        'p-2',
                        rowLayout,
                        disabled && 'pointer-events-none',
                      )}
                      aria-disabled={disabled || undefined}
                      onClick={() => {
                        if (disabled) return;
                        const newValues = p.toFormOnChange(id, p.value || []);
                        p.onChange(newValues);
                      }}
                    >
                      <Checkbox
                        label={name}
                        onChange={noop}
                        checked={(p.value || []).includes(id)}
                        textVariant="body"
                        textSize="large"
                        checkboxVariant="neutral"
                        disabled={disabled}
                      />
                      {status ? (
                        <span
                          className={statusSlot}
                          // Override the parent row's `pointer-events: none` so
                          // the pill stays clickable on disabled (not-ready)
                          // rows — clicking it should reveal WHY the row is
                          // disabled via the readiness popover.
                          style={{ pointerEvents: 'auto' }}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <StatusTag
                            state={status.state}
                            readiness={status.readiness}
                            hideReadinessIndicator
                            onReadinessClick={
                              status.readiness
                                ? (anchor) =>
                                    openPopover(anchor, status.readiness!)
                                : undefined
                            }
                          />
                        </span>
                      ) : null}
                    </div>
                  );
                  return disabled && disabledTooltip ? (
                    <Tooltip
                      key={id}
                      arrow
                      variant="dark"
                      title={disabledTooltip}
                      placement="right"
                      tooltipProps={{ maxWidth: 240 }}
                    >
                      <span>{row}</span>
                    </Tooltip>
                  ) : (
                    row
                  );
                },
              )}
            </div>
          </div>
        </div>
      </ClickAwayListener>
      <ProgramReadinessPopover
        anchor={popoverAnchor}
        readiness={popoverReadiness}
        onClose={closePopover}
      />
    </>
  );
};
